export const BACKGROUND_TASK_EVENT = "fintheon:background-task";

export type BackgroundTaskStatus = "started" | "completed" | "failed";

export interface BackgroundTaskEventDetail {
  id: string;
  title: string;
  detail?: string;
  status: BackgroundTaskStatus;
  source?: "desk" | "chat" | "narrativeflow" | "agent";
}

const ACTIVE_TASKS_KEY = "fintheon:background-tasks:v1";

export function startBackgroundTask(
  task: Omit<BackgroundTaskEventDetail, "status">,
) {
  updateStoredTasks(task.id, task);
  emitBackgroundTask({ ...task, status: "started" });
}

export function finishBackgroundTask(
  task: Omit<BackgroundTaskEventDetail, "status"> & {
    status?: Exclude<BackgroundTaskStatus, "started">;
  },
) {
  removeStoredTask(task.id);
  emitBackgroundTask({ ...task, status: task.status ?? "completed" });
}

export function readActiveBackgroundTasks(): BackgroundTaskEventDetail[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(ACTIVE_TASKS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    window.localStorage.removeItem(ACTIVE_TASKS_KEY);
    return [];
  }
}

function emitBackgroundTask(detail: BackgroundTaskEventDetail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<BackgroundTaskEventDetail>(BACKGROUND_TASK_EVENT, {
      detail,
    }),
  );
}

function updateStoredTasks(
  id: string,
  task: Omit<BackgroundTaskEventDetail, "status">,
) {
  if (typeof window === "undefined") return;
  const next = [
    ...readActiveBackgroundTasks().filter((existing) => existing.id !== id),
    { ...task, status: "started" as const },
  ];
  window.localStorage.setItem(ACTIVE_TASKS_KEY, JSON.stringify(next));
}

function removeStoredTask(id: string) {
  if (typeof window === "undefined") return;
  const next = readActiveBackgroundTasks().filter((task) => task.id !== id);
  if (next.length === 0) {
    window.localStorage.removeItem(ACTIVE_TASKS_KEY);
  } else {
    window.localStorage.setItem(ACTIVE_TASKS_KEY, JSON.stringify(next));
  }
}
